import { useMemo, useState } from 'react';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  getFinancingPlans,
  deleteFinancingPlan,
} from '@/features/financing-plans/api';
import type { FinancingPlanModel } from '@/features/financing-plans/plan';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAppSelector } from '@/store/hooks';
import { cn, formatCurrency } from '@/lib/utils';
import { hasMarkupRate } from '@/lib/markup';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  AlertTriangle,
  Layers,
  Plus,
  Star,
  Calendar,
  Percent,
  DollarSign,
  CheckCircle,
  XCircle,
  Pencil,
  Trash2,
  Timer,
} from '@/lib/icons';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ApiErrorCard } from '@/components/feedback/ApiErrorCard';
import { ListFilterBar, NoFilterResults } from '@/components/filters';
import type { FilterChip } from '@/components/filters';
import { useListFilters } from '@/hooks/use-list-filters';
import { useAuth } from '@/hooks/useAuth';

const PLAN_STATUS_VALUES = ['active', 'inactive', 'all'] as const;
type PlanStatusFilter = (typeof PLAN_STATUS_VALUES)[number];

/**
 * The catalogue accumulates retired plans — nine of twelve on a typical
 * tenant — and a retired plan is never what an operator came to find. The
 * list therefore opens on what is currently sellable; the chips above it
 * carry the retired count, so nothing is hidden without being announced.
 */
const DEFAULT_STATUS: PlanStatusFilter = 'active';

const FREQUENCY_VALUES = ['daily', 'weekly', 'monthly'] as const;
type PlanFrequency = (typeof FREQUENCY_VALUES)[number];
const ALL_FREQUENCIES = 'all';

const financingPlansSearchSchema = z.object({
  q: z.string().optional(),
  // `.catch` rather than `.optional` — an absent or malformed value lands on
  // the default view instead of throwing the route out.
  status: z.enum(PLAN_STATUS_VALUES).catch(DEFAULT_STATUS),
  frequency: z.enum(FREQUENCY_VALUES).optional(),
  page: z.coerce.number().int().min(1).optional(),
});

export const Route = createFileRoute('/_layout/financing-plans/')({
  validateSearch: financingPlansSearchSchema,
  component: FinancingPlansPage,
});

const PAGE_SIZE = 20;

const tierColors: Record<string, string> = {
  A: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400',
  B: 'bg-blue-500/15 text-blue-600 border-blue-500/30 dark:text-blue-400',
  C: 'bg-amber-500/15 text-amber-600 border-amber-500/30 dark:text-amber-400',
  D: 'bg-orange-500/15 text-orange-600 border-orange-500/30 dark:text-orange-400',
  E: 'bg-red-500/15 text-red-600 border-red-500/30 dark:text-red-400',
};

type CardTheme = Readonly<{
  badge: string;
  wash: string;
  border: string;
  accent: string;
}>;

/**
 * Card colour is keyed to the installment frequency, not to the card's
 * position in the grid. Position-keyed colour meant every plan changed hue
 * whenever a filter changed the ordering; keyed to frequency it stays put and
 * says something true — the wash matches the frequency badge.
 */
const frequencyThemes: Record<PlanFrequency, CardTheme> = {
  daily: {
    badge: 'bg-blue-500/15 text-blue-600 border-blue-500/30 dark:text-blue-400',
    wash: 'bg-blue-500/5',
    border: 'border-blue-500/20',
    accent: 'text-blue-600 dark:text-blue-400',
  },
  weekly: {
    badge:
      'bg-violet-500/15 text-violet-600 border-violet-500/30 dark:text-violet-400',
    wash: 'bg-violet-500/5',
    border: 'border-violet-500/20',
    accent: 'text-violet-600 dark:text-violet-400',
  },
  monthly: {
    badge:
      'bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400',
    wash: 'bg-emerald-500/5',
    border: 'border-emerald-500/20',
    accent: 'text-emerald-600 dark:text-emerald-400',
  },
};

/**
 * Retired plans drop the colour wash rather than being dimmed. The previous
 * `opacity-60` pushed the body copy below contrast minimums to say something
 * the Inactive badge already says.
 */
const retiredTheme: CardTheme = {
  badge: 'bg-muted text-muted-foreground border-border',
  wash: '',
  border: 'border-dashed border-border',
  accent: 'text-muted-foreground',
};

const frequencyRank: Record<string, number> = {
  daily: 0,
  weekly: 1,
  monthly: 2,
};

/** Sellable first, then grouped by frequency and shortest term. */
const comparePlans = (a: FinancingPlanModel, b: FinancingPlanModel): number =>
  Number(b.isActive) - Number(a.isActive) ||
  (frequencyRank[a.installmentFrequency] ?? 9) -
    (frequencyRank[b.installmentFrequency] ?? 9) ||
  a.tenureMonths - b.tenureMonths ||
  a.name.localeCompare(b.name);

const matchesTerm = (plan: FinancingPlanModel, term: string): boolean =>
  plan.name.toLowerCase().includes(term) ||
  (plan.description?.toLowerCase().includes(term) ?? false);

function FinancingPlansPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  // Catalogue configuration is admin-only — see lib/auth/admin-scopes.
  // The API refuses the write regardless; this keeps the portal from
  // offering a form that cannot be saved.
  //
  // Read is deliberately open (an agent needs the plan name on an
  // application), so every write control has to be gated individually —
  // reaching this page is not permission to change what is on it.
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('financing_plans', 'create');
  const canUpdate = hasPermission('financing_plans', 'update');
  const canDelete = hasPermission('financing_plans', 'delete');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [deletingPlan, setDeletingPlan] = useState<FinancingPlanModel | null>(
    null
  );
  const currency =
    useAppSelector((state) => state.tenant.settings?.currency) ?? 'USD';

  const {
    searchInput,
    setSearchInput,
    debouncedSearch,
    isSearchPending,
    setFilters,
    reset,
  } = useListFilters({ search, to: '/financing-plans' });

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['financing-plans'],
    queryFn: () => getFinancingPlans(),
    staleTime: 0,
  });

  // The endpoint returns the whole catalogue in one call, so every facet is
  // applied here rather than round-tripped — toggling is instant and the chip
  // counts stay exact.
  const allPlans = useMemo(() => data?.value ?? [], [data]);
  const term = debouncedSearch?.toLowerCase();

  // Search and frequency narrow the catalogue; the status chips then split
  // what is left, which is what lets each chip carry a count reflecting the
  // current search rather than the whole catalogue.
  const scoped = useMemo(
    () =>
      allPlans.filter(
        (plan) =>
          (!term || matchesTerm(plan, term)) &&
          (!search.frequency || plan.installmentFrequency === search.frequency)
      ),
    [allPlans, term, search.frequency]
  );

  const scopedActive = scoped.filter((plan) => plan.isActive).length;

  const visiblePlans = useMemo(() => {
    const byStatus =
      search.status === 'all'
        ? scoped
        : scoped.filter(
            (plan) => plan.isActive === (search.status === 'active')
          );
    return [...byStatus].sort(comparePlans);
  }, [scoped, search.status]);

  const totalActive = allPlans.filter((plan) => plan.isActive).length;

  // Counted against the default view, not against "no params at all" — Reset
  // returns the operator to the sellable list, which is where they started.
  const appliedCount =
    (debouncedSearch ? 1 : 0) +
    (search.status !== DEFAULT_STATUS ? 1 : 0) +
    (search.frequency ? 1 : 0);

  const totalPages = Math.max(1, Math.ceil(visiblePlans.length / PAGE_SIZE));
  const page = Math.min(Math.max(1, search.page ?? 1), totalPages);
  const paginatedPlans = visiblePlans.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );
  const startItem = visiblePlans.length > 0 ? (page - 1) * PAGE_SIZE + 1 : 0;
  const endItem = Math.min(page * PAGE_SIZE, visiblePlans.length);

  const statusChips: ReadonlyArray<FilterChip<PlanStatusFilter>> = [
    { value: 'active', label: t('common.status.active'), count: scopedActive },
    {
      value: 'inactive',
      label: t('common.status.inactive'),
      count: scoped.length - scopedActive,
    },
    {
      value: 'all',
      label: t('financingPlans.filters.all'),
      count: scoped.length,
    },
  ];

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteFinancingPlan(id),
    onSuccess: (result) => {
      if (result.succeeded) {
        queryClient.invalidateQueries({ queryKey: ['financing-plans'] });
        toast({
          title: t('common.success'),
          description: t('financingPlans.toast.deleted'),
        });
      } else {
        toast({
          title: t('common.error'),
          description:
            result.errors?.[0] ?? t('financingPlans.toast.deleteFailed'),
          variant: 'destructive',
        });
      }
      setDeletingPlan(null);
    },
  });

  const summary = [
    {
      label: t('financingPlans.summary.totalPlans'),
      value: allPlans.length,
      icon: Layers,
      color: 'from-blue-500 to-blue-600',
    },
    {
      label: t('financingPlans.summary.activePlans'),
      value: totalActive,
      icon: CheckCircle,
      color: 'from-emerald-500 to-emerald-600',
    },
    {
      label: t('financingPlans.summary.inactivePlans'),
      value: allPlans.length - totalActive,
      icon: XCircle,
      color: 'from-gray-400 to-gray-500',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {t('financingPlans.title')}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {t('financingPlans.description')}
          </p>
        </div>
        {canCreate && (
          <Button
            className="bg-gradient-to-r from-primary to-primary/85 hover:from-primary/90 hover:to-primary/75 text-primary-foreground shadow-md hover:shadow-lg transition-all duration-200"
            onClick={() => navigate({ to: '/financing-plans/new' })}
          >
            <Plus className="w-4 h-4 mr-2" /> {t('financingPlans.createPlan')}
          </Button>
        )}
      </div>

      {/* Summary — the whole catalogue, deliberately unaffected by the
          filters below, so the operator can always see what is being hidden. */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {summary.map((item, idx) => (
          <Card
            key={item.label}
            className={cn(
              'relative overflow-hidden border-0 shadow-md',
              idx === 2 && 'col-span-2 lg:col-span-1'
            )}
          >
            <div
              className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-[0.07]`}
            />
            <CardContent className="p-4 flex items-center gap-3 relative">
              <div
                className={`p-2.5 rounded-xl bg-gradient-to-br ${item.color} text-white shadow-lg`}
              >
                <item.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold tabular-nums">
                  {isLoading ? '—' : item.value}
                </p>
                <p className="text-xs text-muted-foreground">{item.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <ListFilterBar<PlanStatusFilter>
        chips={statusChips}
        chipValue={search.status}
        onChipChange={(value) => setFilters({ status: value })}
        chipsAriaLabel={t('financingPlans.filters.statusLabel')}
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder={t('filters.searchPlaceholder.financingPlans')}
        isSearchPending={isSearchPending}
        activeCount={appliedCount}
        onReset={reset}
        isPending={isFetching}
      >
        <Select
          value={search.frequency ?? ALL_FREQUENCIES}
          onValueChange={(next) =>
            setFilters({
              frequency: next === ALL_FREQUENCIES ? undefined : next,
            })
          }
        >
          <SelectTrigger
            className="h-10 w-full sm:w-[180px]"
            aria-label={t('financingPlans.filters.frequencyLabel')}
          >
            <Timer className="me-2 h-4 w-4 shrink-0 text-muted-foreground" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_FREQUENCIES}>
              {t('financingPlans.filters.allFrequencies')}
            </SelectItem>
            {FREQUENCY_VALUES.map((frequency) => (
              <SelectItem key={frequency} value={frequency}>
                {t(`financingPlans.frequency.${frequency}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </ListFilterBar>

      {/* Plans Grid */}
      {isLoading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-56 w-full rounded-xl" />
          ))}
        </div>
      ) : isError ? (
        <ApiErrorCard
          error={error}
          onRetry={() => refetch()}
          title={t('financingPlans.loadFailed')}
        />
      ) : visiblePlans.length === 0 ? (
        <NoFilterResults
          isFiltered={allPlans.length > 0}
          emptyTitle={t('financingPlans.empty.title')}
          emptyDescription={t('financingPlans.empty.description')}
          icon={Layers}
          onReset={reset}
          action={
            canCreate ? (
              <Button onClick={() => navigate({ to: '/financing-plans/new' })}>
                <Plus className="w-4 h-4 mr-2" />{' '}
                {t('financingPlans.createPlan')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          {visiblePlans.length !== allPlans.length && (
            <p className="text-sm text-muted-foreground">
              {t('financingPlans.showing', {
                shown: visiblePlans.length,
                total: allPlans.length,
              })}
            </p>
          )}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {paginatedPlans.map((plan) => {
              const theme = plan.isActive
                ? (frequencyThemes[
                    plan.installmentFrequency as PlanFrequency
                  ] ?? retiredTheme)
                : retiredTheme;
              return (
                <Card
                  key={plan.id}
                  className={cn(
                    'relative flex h-full flex-col overflow-hidden border shadow-md transition-shadow duration-200 hover:shadow-lg',
                    theme.border
                  )}
                >
                  {theme.wash && (
                    <div className={cn('absolute inset-0', theme.wash)} />
                  )}
                  <CardHeader className="relative pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-lg leading-snug">
                        {plan.name}
                      </CardTitle>
                      <div className="flex shrink-0 items-center gap-1">
                        <Badge className={theme.badge}>
                          {t(
                            `financingPlans.frequency.${plan.installmentFrequency}`,
                            { defaultValue: plan.installmentFrequency }
                          )}
                        </Badge>
                        {plan.isActive ? (
                          <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400">
                            {t('common.status.active')}
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-500/15 text-gray-600 border-gray-500/30 dark:text-gray-400">
                            {t('common.status.inactive')}
                          </Badge>
                        )}
                      </div>
                    </div>
                    {/* Reserved even when empty, so the metric block below
                        lines up across every card in a row. */}
                    <p className="mt-1 line-clamp-2 min-h-[2.5rem] text-sm text-muted-foreground">
                      {plan.description ?? ''}
                    </p>
                  </CardHeader>
                  <CardContent className="relative flex flex-1 flex-col space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center gap-2">
                        <Calendar className={`w-4 h-4 ${theme.accent}`} />
                        <div>
                          <p className="text-xs text-muted-foreground">
                            {t('financingPlans.card.tenure')}
                          </p>
                          <p className="font-semibold text-sm">
                            {plan.tenureMonths}{' '}
                            {t('financingPlans.card.months')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <DollarSign className={`w-4 h-4 ${theme.accent}`} />
                        <div>
                          <p className="text-xs text-muted-foreground">
                            {t('financingPlans.card.downPayment')}
                          </p>
                          {/* Only plans saved before the down payment became
                              mandatory can reach 0% — call them out rather
                              than presenting 0% as a valid term. */}
                          {plan.downPaymentPercent > 0 ? (
                            <p className="font-semibold text-sm">
                              {plan.downPaymentPercent}%
                            </p>
                          ) : (
                            <p className="flex items-center gap-1 font-semibold text-sm text-amber-600 dark:text-amber-400">
                              <AlertTriangle className="h-3.5 w-3.5" />
                              {t('financingPlans.card.downPaymentMissing')}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <DollarSign className={`w-4 h-4 ${theme.accent}`} />
                        <div>
                          <p className="text-xs text-muted-foreground">
                            {t('financingPlans.card.maxFinance')}
                          </p>
                          {/* Null is an uncapped plan — the backend only
                              enforces a ceiling when one is set. Rendering it
                              as $0.00 said the opposite of what it means. */}
                          <p className="font-semibold text-sm">
                            {plan.maxFinanceAmount === null
                              ? t('financingPlans.card.noMaxFinance')
                              : formatCurrency(plan.maxFinanceAmount, currency)}
                          </p>
                        </div>
                      </div>
                      {hasMarkupRate(plan.markupRate) && (
                        <div className="flex items-center gap-2">
                          <Percent className={`w-4 h-4 ${theme.accent}`} />
                          <div>
                            <p className="text-xs text-muted-foreground">
                              {t('financingPlans.card.markup')}
                            </p>
                            <p className="font-semibold text-sm">
                              {plan.markupRate}%
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="mt-auto space-y-3">
                      <div className="pt-2 border-t flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">
                          {t('financingPlans.card.minCreditTier')}
                        </span>
                        <Badge className={tierColors[plan.minCreditTier] ?? ''}>
                          <Star className="w-3 h-3 mr-1" />
                          {t('financingPlans.card.tier', {
                            tier: plan.minCreditTier,
                          })}
                        </Badge>
                      </div>
                      {/* Action buttons — the whole row disappears for an
                          operator who may do neither, rather than leaving an
                          empty bordered strip on the card. */}
                      {(canUpdate || canDelete) && (
                        <div className="pt-2 border-t flex items-center gap-2">
                          {canUpdate && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                navigate({
                                  to: '/financing-plans/$planId/edit',
                                  params: { planId: plan.id },
                                })
                              }
                            >
                              <Pencil className="w-3.5 h-3.5 mr-1" />
                              {t('common.actions.edit')}
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setDeletingPlan(plan)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="w-3.5 h-3.5 mr-1" />
                              {t('common.actions.delete')}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Pagination */}
          {visiblePlans.length > PAGE_SIZE && (
            <div className="flex items-center justify-between pt-4">
              <p className="text-sm text-muted-foreground">
                {t('financingPlans.pagination.showing', {
                  start: startItem,
                  end: endItem,
                  total: visiblePlans.length,
                })}
              </p>
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() =>
                        setFilters({ page: Math.max(1, page - 1) })
                      }
                      className={
                        page <= 1
                          ? 'pointer-events-none opacity-50'
                          : 'cursor-pointer'
                      }
                    />
                  </PaginationItem>
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <PaginationItem key={i}>
                      <PaginationLink
                        isActive={page === i + 1}
                        onClick={() => setFilters({ page: i + 1 })}
                        className="cursor-pointer"
                      >
                        {i + 1}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                  <PaginationItem>
                    <PaginationNext
                      onClick={() =>
                        setFilters({ page: Math.min(totalPages, page + 1) })
                      }
                      className={
                        page >= totalPages
                          ? 'pointer-events-none opacity-50'
                          : 'cursor-pointer'
                      }
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </>
      )}

      {/* Delete confirmation */}
      <AlertDialog
        open={!!deletingPlan}
        onOpenChange={(open) => {
          if (!open) setDeletingPlan(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('financingPlans.delete.title')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('financingPlans.delete.description', {
                name: deletingPlan?.name,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                deletingPlan && deleteMutation.mutate(deletingPlan.id)
              }
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending
                ? t('common.actions.deleting')
                : t('financingPlans.delete.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
